/**
 * Password dialog for encrypting/decrypting workspace files
 * Supports optional password (blank = obfuscation only)
 */

import React, { useState, useEffect } from "react";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import { Message } from "primereact/message";
import { Tooltip } from "primereact/tooltip";
import PropTypes from "prop-types";

const PasswordDialog = ({
  visible,
  onHide,
  onConfirm,
  title,
  message,
  mode = "export", // 'export' or 'import'
  passwordRequired = false, // Only for import mode
}) => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (visible) {
      setPassword("");
      setShowPassword(false);
      setError("");
    }
  }, [visible]);

  const handleConfirm = () => {
    // For export: allow blank password (obfuscation mode)
    // For import with passwordRequired: require password
    if (mode === "import" && passwordRequired && !password.trim()) {
      setError("Senha obrigatória para este arquivo");
      return;
    }

    setError("");
    onConfirm(password); // Can be blank for export
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleConfirm();
    }
  };

  const footer = (
    <div>
      <Button
        label="Cancelar"
        icon="pi pi-times"
        onClick={onHide}
        className="p-button-text"
        data-evo-id="passworddialog-btn-cancel"
      />
      <Button
        label={mode === "export" ? "Exportar" : "Importar"}
        icon={mode === "export" ? "pi pi-download" : "pi pi-upload"}
        onClick={handleConfirm}
        data-evo-id="passworddialog-btn-confirm"
      />
    </div>
  );

  return (
    <>
      <Tooltip target=".password-toggle-btn" />

      <Dialog
        header={title}
        visible={visible}
        style={{ width: "min(450px, 92vw)" }}
        onHide={onHide}
        footer={footer}
        modal
      >
        <div className="p-fluid">
          {message && <p style={{ marginBottom: "1rem" }}>{message}</p>}

          {mode === "export" && (
            <Message
              severity="info"
              text="Deixe em branco para proteção básica (sem senha)"
              style={{ marginBottom: "1rem" }}
            />
          )}

          <div className="p-inputgroup">
            <InputText
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={
                mode === "export" ? "Senha (opcional)" : "Digite a senha"
              }
              autoFocus
              data-evo-id="passworddialog-input-password"
            />
            <Button
              icon={showPassword ? "pi pi-eye-slash" : "pi pi-eye"}
              onClick={() => setShowPassword(!showPassword)}
              className="p-button-secondary password-toggle-btn"
              type="button"
              data-pr-tooltip={showPassword ? "Ocultar senha" : "Mostrar senha"}
              data-pr-position="top"
              data-evo-id="passworddialog-btn-toggle-password"
            />
          </div>

          {error && (
            <Message
              severity="error"
              text={error}
              style={{ marginTop: "0.5rem" }}
            />
          )}

          {mode === "export" && password.trim() && (
            <small
              style={{
                marginTop: "0.5rem",
                display: "block",
                color: "var(--text-color-secondary)",
              }}
            >
              ⚠️ Anote a senha - não será possível recuperar sem ela
            </small>
          )}
        </div>
      </Dialog>
    </>
  );
};

PasswordDialog.propTypes = {
  visible: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onConfirm: PropTypes.func,
  title: PropTypes.string.isRequired,
  message: PropTypes.string,
  mode: PropTypes.oneOf(["export", "import"]),
  passwordRequired: PropTypes.bool,
};

export default PasswordDialog;
